// useCardViewRefresh Hook
// Date: December 10, 2025
// Purpose: Generic hook to handle card view refresh after CRUD operations
//
// This hook provides a standardized way to refresh card views after create, update, delete, or other mutations.
// It uses a refreshKey pattern that triggers useEffect dependencies to reload card data.

import { useState, useCallback } from 'react';

/**
 * Hook to manage card view refresh state
 * 
 * @param viewMode - Current view mode ('cards' | 'table')
 * @returns Object with refreshKey and refresh function
 * 
 * @example
 * ```tsx
 * const { refreshKey, refresh } = useCardViewRefresh(viewMode);
 * 
 * useEffect(() => {
 *   if (viewMode === 'cards') {
 *     loadCardData();
 *   }
 * }, [viewMode, refreshKey]);
 * 
 * const handleDelete = async (item: any) => {
 *   await deleteItem(item.id);
 *   refresh(); // Triggers card data reload
 * };
 * ```
 */
export const useCardViewRefresh = (_viewMode: 'cards' | 'table') => {
  const [refreshKey, setRefreshKey] = useState(0);

  /**
   * Refresh card view by incrementing refreshKey
   * The useEffect that depends on refreshKey will handle the viewMode check
   * This ensures refresh always works regardless of when it's called
   * 
   * Note: viewMode parameter is kept for API consistency but not used internally
   * as the useEffect in components handles the viewMode check
   */
  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return {
    refreshKey,
    refresh
  };
};

